/**
 * Supabase Client Configuration
 *
 * Centralized Supabase client for authentication and database access
 * Used across all features: Wallet Tracker, Advice Bot, Trading Bot
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { syncSecureStorage } from '../../services/secureTokenStorage';

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables (production-safe, no console.log)
const isMissingEnv = !supabaseUrl || !supabaseAnonKey;

/**
 * Main Supabase client instance
 *
 * Features:
 * - Auto session persistence in localStorage
 * - Auto token refresh
 * - Session detection in URL (for OAuth redirects)
 * - Real-time subscriptions enabled
 */
export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
  auth: {
    // Store session with encryption
    persistSession: true,

    // Auto-refresh tokens before expiration
    autoRefreshToken: true,

    // Detect session in URL after OAuth redirect
    detectSessionInUrl: true,

    // Storage key for session
    storageKey: 'wealthhub-auth',

    // Use secure storage (in-memory + encrypted localStorage)
    // This prevents XSS attacks from directly accessing tokens
    storage: syncSecureStorage,

    // Shorter session duration for security
    flowType: 'pkce',
  },

  // Global settings
  global: {
    headers: {
      'x-application-name': 'Edouard.ai Crypto',
    },
  },

  // Enable real-time subscriptions
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

/**
 * Helper: Get current authenticated user
 */
export const getCurrentUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    console.error('Error getting current user:', error);
    return null;
  }

  return user;
};

/**
 * Helper: Get current session
 */
export const getCurrentSession = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    console.error('Error getting session:', error);
    return null;
  }

  return session;
};

/**
 * Helper: Sign out
 */
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();

  if (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Helper: Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const session = await getCurrentSession();
  return !!session;
};

/**
 * Helper: Get user profile (extended data)
 */
export const getUserProfile = async () => {
  const user = await getCurrentUser();

  if (!user) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return data;
};

/**
 * Helper: Update user profile
 */
export const updateUserProfile = async (updates: {
  username?: string;
  avatar_url?: string;
  settings?: Record<string, any>;
}) => {
  const user = await getCurrentUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', user.id)
    .select()
    .single();

  if (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }

  return data;
};

// Export type for use in other files
export type SupabaseClient = typeof supabase;
